import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Row-level focus reporting and Row-field propagation. The context
 * carries:
 *
 * - `reporter` — focus / blur events from child inputs to a parent row
 *   (FormRow, FieldRow, PanelFieldRow) so the row owns the visible
 *   focus border.
 * - `controlId` / `messageId` / `error` / `hint` / `disabled` — populated
 *   by `FormRow` (ADR-0075) so wrapped inputs can:
 *   - Set their own `id` to `controlId` (web) — `<label htmlFor>` pairs.
 *   - Set `aria-describedby` to `messageId` (web) so screen readers
 *     announce the error / hint.
 *   - Apply the Phase 2 `error` variant when `error === true`.
 *   - Apply `disabled` when `disabled === true`.
 *
 * Backward compatibility: the value of the context can still be a plain
 * `RowFocusReporter` function. `useRowFocusReporter` returns just the
 * reporter; `useRowFocusFieldContext` returns the full FormRow slice.
 * Pre-existing FormRow / FieldRow callers don't need to change.
 */
export type RowFocusReporter = (focused: boolean) => void;

export interface RowFocusFieldContext {
  reporter: RowFocusReporter | null;
  controlId?: string;
  messageId?: string;
  error?: boolean;
  hint?: string;
  disabled?: boolean;
}

// Existing callers (FormRow, FieldRow) pass a bare reporter function via
// RowFocusContext.Provider. Newer field-context callers pass a value object.
// Accept both for backward compatibility.
export type RowFocusContextValue = RowFocusReporter | RowFocusFieldContext | null;

export const RowFocusContext = createContext<RowFocusContextValue>(null);

function isFieldContext(v: RowFocusContextValue): v is RowFocusFieldContext {
  return typeof v === 'object' && v !== null && !(v instanceof Function);
}

/**
 * Returns just the focus reporter — the slice every input needs to tell
 * its parent row to highlight. Both context shapes are supported.
 */
export function useRowFocusReporter(): RowFocusReporter | null {
  const v = useContext(RowFocusContext);
  if (v == null) return null;
  if (isFieldContext(v)) return v.reporter;
  return v;
}

/**
 * Returns the full FormRow slice (controlId, messageId, error, etc.).
 * Inputs that need to wire ARIA + apply the error variant call this.
 * Falls back to an empty record if the parent is a legacy reporter-only
 * provider.
 */
export function useRowFocusFieldContext(): RowFocusFieldContext {
  const v = useContext(RowFocusContext);
  if (v == null) return { reporter: null };
  if (isFieldContext(v)) return v;
  return { reporter: v };
}

/**
 * Hook for row containers. Returns the `focused` flag to drive the
 * border + a `reporter` to feed into `RowFocusContext.Provider`. A
 * ref-counted approach keeps the row "focused" while focus transitions
 * between sibling inputs (date → time), avoiding a flicker between
 * blur and the next focus.
 */
export function useRowFocusState(): { focused: boolean; reporter: RowFocusReporter } {
  const countRef = useRef(0);
  const [focused, setFocused] = useState(false);
  const reporter = useCallback<RowFocusReporter>((next) => {
    countRef.current = Math.max(0, countRef.current + (next ? 1 : -1));
    setFocused(countRef.current > 0);
  }, []);
  return { focused, reporter };
}

/**
 * Combines `useRowFocusState` (the reporter + focused flag) with the
 * FormRow slice — used by `FormRow` itself. Returned `value` is
 * memoized so context consumers don't re-render on parent re-renders
 * unless a field-level prop actually changed.
 */
export function useFieldFocusContext(extra: Omit<RowFocusFieldContext, 'reporter'>): {
  focused: boolean;
  value: RowFocusFieldContext;
} {
  const { focused, reporter } = useRowFocusState();
  const value = useMemo<RowFocusFieldContext>(
    () => ({ reporter, ...extra }),
    // Spread is intentional — useMemo identity tracks the slice's keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reporter, extra.controlId, extra.messageId, extra.error, extra.hint, extra.disabled],
  );
  return { focused, value };
}
